import { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { useAdminRbacStore } from '../../../store/adminRbacStore';
import { useAuthStore } from '../../../store/authStore';
import { useRbacStore } from '../../../store/rbacStore';
import { ShieldCheck, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

/** Actions shown first, in this order; any others follow alphabetically. */
const ACTION_ORDER = ['View', 'Create', 'Edit', 'Delete'];
const SCOPES = ['Self', 'Team', 'Company'] as const;

function isSuperAdmin(name: string) {
  return /^super\s*admin$/i.test(name);
}

function orderedActions(actions: string[]): string[] {
  const known = ACTION_ORDER.filter((a) => actions.includes(a));
  const rest = actions.filter((a) => !ACTION_ORDER.includes(a)).sort((a, b) => a.localeCompare(b));
  return [...known, ...rest];
}

export function RolesPermissions() {
  const {
    roles, catalog, rolesLoading, catalogLoading,
    selectedRoleId, grants, grantsLoading, dirty, saving, error,
    hubCatalog, roleHubs, hubsDirty,
    loadRoles, loadCatalog, loadHubCatalog, selectRole,
    togglePermission, toggleResource, setResourceScope, save,
    toggleHub, saveHubs,
  } = useAdminRbacStore();

  const myRoles = useAuthStore((s) => s.user?.roles || []);
  const reloadPermissions = useRbacStore((s) => s.reloadPermissions);

  const [query, setQuery] = useState('');

  useEffect(() => {
    loadRoles();
    loadCatalog();
    loadHubCatalog();
  }, [loadRoles, loadCatalog, loadHubCatalog]);

  const selectedRole = roles.find((r) => r.id === selectedRoleId);
  const locked = selectedRole ? isSuperAdmin(selectedRole.name) : false;

  const grantedCount = Object.keys(grants).length;

  const filteredCatalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog
      .map((m) => ({
        ...m,
        resources: m.resources.filter(
          (r) => r.name.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
        ),
      }))
      .filter((m) => m.resources.length > 0);
  }, [catalog, query]);

  const handleSave = async () => {
    if (!selectedRole) return;
    try {
      // Save module (hub) access and feature permissions together.
      if (hubsDirty) await saveHubs();
      let grantedMsg = '';
      if (dirty) {
        const result = await save();
        grantedMsg = ` · ${result.granted} permissions`;
      }
      toast.success(`Saved — ${selectedRole.name}${grantedMsg}`);
      // If the edited role is one of mine, refresh my own access so the app
      // launcher, hub routes and sidebar reflect the change without re-login.
      if (myRoles.includes(selectedRole.name)) {
        await reloadPermissions();
      }
    } catch {
      toast.error('Could not save changes');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Roles &amp; Permissions</h1>
          <p className="text-muted-foreground mt-1">Map features to each role — toggle a feature to show or hide it, and set which actions the role can perform.</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-strong">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ===== Role list ===== */}
        <div className="lg:col-span-1 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground px-1">Roles</p>
          {rolesLoading && (
            <div className="flex items-center gap-2 px-1 py-3 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading roles…
            </div>
          )}
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {roles.map((role) => {
              const active = selectedRoleId === role.id;
              return (
                <Card
                  key={role.id}
                  className={`cursor-pointer transition-all ${active ? 'border-primary/50 bg-primary/5' : 'hover:border-border'}`}
                  onClick={() => selectRole(role.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-center gap-2">
                      <p className={`text-sm font-semibold ${active ? 'text-primary' : 'text-foreground'}`}>{role.name}</p>
                      {isSuperAdmin(role.name) && (
                        <Badge variant="default" className="shrink-0">Full access</Badge>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{role.description}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ===== Feature matrix for the selected role ===== */}
        <div className="lg:col-span-2">
          {!selectedRole ? (
            <Card>
              <CardContent className="p-10 text-center text-muted-foreground">
                <ShieldCheck className="mx-auto size-8 mb-3 opacity-50" />
                <p className="text-sm">Select a role to view and edit its feature access.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center gap-3">
                <div className="min-w-0">
                  <h2 className="text-foreground font-display text-section-title truncate">{selectedRole.name}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {locked ? 'This role always has full access and cannot be edited.' : `${roleHubs.length} modules · ${grantedCount} permissions granted`}
                  </p>
                </div>
                {!locked && (
                  <Button variant="primary" size="sm" disabled={(!dirty && !hubsDirty) || saving} onClick={handleSave}>
                    {saving ? <Loader2 className="size-4 animate-spin" /> : 'Apply Changes'}
                  </Button>
                )}
              </CardHeader>

              <CardContent className="p-0">
                {/* Search */}
                {!locked && (
                  <div className="px-6 py-3 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search features or modules…"
                        className="w-full bg-background border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                )}

                {/* Module (hub) access — which top-level modules this role can open */}
                {!locked && !grantsLoading && (
                  <div className="px-6 py-4 border-b border-border bg-background/40">
                    <p className="text-xs font-semibold text-muted-foreground mb-3">Module access — which modules this role can open</p>
                    <div className="flex flex-wrap gap-2">
                      {hubCatalog.map((hub) => {
                        const on = roleHubs.includes(hub.key);
                        return (
                          <button
                            key={hub.key}
                            type="button"
                            onClick={() => toggleHub(hub.key, !on)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              on
                                ? 'border-primary/40 bg-primary/10 text-primary'
                                : 'border-border bg-card text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <span className={`size-1.5 rounded-full ${on ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
                            {hub.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(catalogLoading || grantsLoading) ? (
                  <div className="flex items-center gap-2 px-6 py-10 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" /> Loading features…
                  </div>
                ) : locked ? (
                  <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                    Super Admin can access every feature in every module.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredCatalog.map((mod) => (
                      <div key={mod.id} className="py-2">
                        <p className="px-6 py-2 text-xs font-semibold text-primary bg-primary/5">{mod.name}</p>
                        <div className="divide-y divide-border-12">
                          {mod.resources.map((resrc) => {
                            const actions = orderedActions(resrc.permissions.map((p) => p.action));
                            const anyOn = resrc.permissions.some((p) => grants[p.id]);
                            const currentScope = (resrc.permissions.map((p) => grants[p.id]).find(Boolean) as string) || 'Company';
                            return (
                              <div key={resrc.id} className="px-6 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                                {/* Master toggle */}
                                <label className="flex items-center gap-2 cursor-pointer min-w-[180px]">
                                  <span className="relative inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="sr-only peer"
                                      checked={anyOn}
                                      onChange={(e) => toggleResource(resrc, e.target.checked)}
                                    />
                                    <span className="w-9 h-5 bg-secondary rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border after:border-border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></span>
                                  </span>
                                  <span className="text-sm font-semibold text-foreground">{resrc.name}</span>
                                </label>

                                {/* Per-action checkboxes */}
                                <div className="flex flex-wrap gap-3 flex-1">
                                  {resrc.permissions
                                    .slice()
                                    .sort((a, b) => actions.indexOf(a.action) - actions.indexOf(b.action))
                                    .map((p) => (
                                      <label key={p.id} className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="w-3.5 h-3.5 accent-primary rounded border-border"
                                          checked={!!grants[p.id]}
                                          onChange={(e) => togglePermission(p.id, e.target.checked, currentScope as any)}
                                        />
                                        <span className="text-xs text-muted-foreground font-medium">{p.action}</span>
                                      </label>
                                    ))}
                                </div>

                                {/* Scope */}
                                <select
                                  value={currentScope}
                                  disabled={!anyOn}
                                  onChange={(e) => setResourceScope(resrc, e.target.value as any)}
                                  className="bg-card border border-border rounded-md px-2 py-1 text-xs text-foreground focus:outline-none disabled:opacity-40"
                                  title="Which records this role can act on"
                                >
                                  {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {filteredCatalog.length === 0 && (
                      <div className="px-6 py-10 text-center text-sm text-muted-foreground">No features match “{query}”.</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
